import apiClient from '@/services/api-client';
import { ApiResponse } from '@/services/types';
import {
  ParameterSchema,
  ParameterListResponse,
  ParameterListParams,
  ParameterMutationPayload,
  CheckerListResponse,
  CheckerListParams,
  CheckerActionPayload,
  RevisionPayload,
  ParameterFieldOption,
  ParameterFieldOptionsResponse,
} from '@/services/types/parameter-types';
import { API_ENDPOINTS } from '@/utils/constants/end-points';

export const getParameterSchema = async (
  tableName: string
): Promise<ParameterSchema> => {
  const res = await apiClient.get<ApiResponse<ParameterSchema>>(
    `${API_ENDPOINTS.PARAMETERS}/schema/${tableName}`
  );
  return res.data.data;
};

export const getParameterList = async (
  params: ParameterListParams
): Promise<ParameterListResponse> => {
  const { tableName, ...queryParams } = params;
  const res = await apiClient.get<ApiResponse<ParameterListResponse>>(
    `${API_ENDPOINTS.PARAMETERS}/${tableName}`,
    { params: queryParams }
  );
  return res.data.data;
};

export const getParameterById = async (
  tableName: string,
  id: number
): Promise<Record<string, any>> => {
  const res = await apiClient.get<ApiResponse<Record<string, any>>>(
    `${API_ENDPOINTS.PARAMETERS}/${tableName}/${id}`
  );
  return res.data.data;
};

export const createParameter = async (
  tableName: string,
  payload: ParameterMutationPayload | FormData
): Promise<any> => {
  const isFormData = payload instanceof FormData;
  const res = await apiClient.post<ApiResponse<any>>(
    `${API_ENDPOINTS.PARAMETERS}/${tableName}`,
    payload,
    isFormData
      ? {
          headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'multipart/form-data',
          },
        }
      : undefined
  );
  return res.data;
};

export const updateParameter = async (
  tableName: string,
  id: number,
  payload: ParameterMutationPayload | FormData
): Promise<any> => {
  const isFormData = payload instanceof FormData;
  const res = await apiClient.put<ApiResponse<any>>(
    `${API_ENDPOINTS.PARAMETERS}/${tableName}/${id}`,
    payload,
    isFormData
      ? {
          headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'multipart/form-data',
          },
        }
      : undefined
  );
  return res.data;
};

export const getCheckerParameterList = async (
  params: CheckerListParams
): Promise<CheckerListResponse> => {
  const { tableName, ...queryParams } = params;
  const res = await apiClient.get<ApiResponse<CheckerListResponse>>(
    `${API_ENDPOINTS.MAINTENANCE}/parameters/${tableName}`,
    { params: queryParams }
  );
  return res.data.data;
};

export const performCheckerAction = async (
  payload: CheckerActionPayload
): Promise<any> => {
  const res = await apiClient.post<ApiResponse<any>>(
    `${API_ENDPOINTS.PARAMETERS}/checker-action`,
    payload
  );
  return res.data;
};

export const reviseParameter = async (
  txnId: string,
  payload: RevisionPayload | FormData
): Promise<any> => {
  const isFormData = payload instanceof FormData;
  const res = await apiClient.put<ApiResponse<any>>(
    `${API_ENDPOINTS.PARAMETERS}/revision/${txnId}`,
    payload,
    isFormData
      ? {
          headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'multipart/form-data',
          },
        }
      : undefined
  );
  return res.data;
};

export const getParameterCounts = async (
  workflowStatusIds: string
): Promise<Record<string, number>> => {
  const res = await apiClient.get<
    ApiResponse<Array<{ tableName: string; count: number }>>
  >(`${API_ENDPOINTS.MAINTENANCE}/parameters/counts`, {
    params: { workflowStatusId: workflowStatusIds },
  });

  // Transform array response to Record<string, number>
  const countsMap: Record<string, number> = {};
  if (Array.isArray(res.data.data)) {
    res.data.data.forEach(item => {
      countsMap[item.tableName] = item.count;
    });
  }
  return countsMap;
};

export const getParameterTransactionById = async (
  txnId: string
): Promise<any> => {
  const res = await apiClient.get<ApiResponse<any>>(
    `${API_ENDPOINTS.MAINTENANCE}/${txnId}`
  );
  return res.data.data;
};

export const deleteParameter = async (
  tableName: string,
  id: number
): Promise<any> => {
  const res = await apiClient.delete<ApiResponse<any>>(
    `${API_ENDPOINTS.PARAMETERS}/${tableName}/${id}`
  );
  return res.data;
};

export const changeParameterStatus = async (
  tableName: string,
  id: number,
  statusLookupId: number
): Promise<any> => {
  const res = await apiClient.put<ApiResponse<any>>(
    `${API_ENDPOINTS.PARAMETERS}/${tableName}/${id}/status`,
    { statusId: statusLookupId } // Sending as payload, though it could be a query param depending on backend
  );
  return res.data;
};

// ─── File Upload APIs ───

export interface FileUploadResponse {
  internalName: string;
  actualName: string;
  key: string;
  url: string;
}

export const uploadParameterFile = async (
  file: File,
  type: string
): Promise<FileUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiClient.post<ApiResponse<FileUploadResponse>>(
    `${API_ENDPOINTS.fileUpload}/upload`,
    formData,
    {
      params: { type },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return res.data.data;
};

export const removeParameterFile = async (key: string): Promise<any> => {
  const res = await apiClient.delete<ApiResponse<any>>(
    `${API_ENDPOINTS.fileUpload}/remove-file`,
    { params: { key } }
  );
  return res.data;
};

/**
 * A fresh, short-lived link to a stored file (`get-file`), or null when the
 * response carries none.
 */
export const getParameterFileUrl = async (
  key: string
): Promise<string | null> => {
  const res = await apiClient.get<ApiResponse<any>>(
    `${API_ENDPOINTS.fileUpload}/get-file`,
    { params: { key } }
  );
  const data = res.data?.data;
  return data?.key || data?.url || null;
};

export const downloadParameterFile = async (key: string): Promise<void> => {
  const fileUrl = await getParameterFileUrl(key);
  if (!fileUrl) {
    throw new Error('File URL not found in response');
  }
  window.open(fileUrl, '_blank');
};

export const getParameterFieldOptions = async (
  fieldId: number,
  filterValue?: string
): Promise<ParameterFieldOption[]> => {
  const params: Record<string, any> = {};
  if (filterValue !== undefined) {
    params.filterValue = filterValue;
  }
  const res = await apiClient.get<ParameterFieldOptionsResponse>(
    `${API_ENDPOINTS.PARAMETERS}/fields/${fieldId}/options`,
    { params }
  );
  return res.data.data;
};
